import { PreSalesQuestion, PreSalesValidationRule, PreSalesVisibleIfCondition, PreSalesWarningCheck } from '../dto/model';

export type PreSalesAnswers = Record<string, string | number | string[] | undefined>;

const getAnswer = (answers: PreSalesAnswers, questionKey: string): unknown => answers[questionKey];

const arePreSalesValuesEqual = (left: unknown, right: unknown): boolean => {
  if (left === right) return true;
  const leftIsComparable = typeof left === 'string' || typeof left === 'number';
  const rightIsComparable = typeof right === 'string' || typeof right === 'number';
  return leftIsComparable && rightIsComparable && String(left) === String(right);
};

export const isPreSalesAnswerEmpty = (value: unknown): boolean =>
  value === undefined || value === null || value === '' || (typeof value === 'string' && value.trim().length === 0) || (Array.isArray(value) && value.length === 0);

export const evaluatePreSalesVisibleIf = (condition: PreSalesVisibleIfCondition | undefined | null, answers: PreSalesAnswers): boolean => {
  if (condition == null) return true;

  switch (condition.op) {
    case 'always':
      return true;
    case 'never':
      return false;
    case 'eq': {
      const answer = getAnswer(answers, condition.questionKey);
      return arePreSalesValuesEqual(answer, condition.value);
    }
    case 'or':
      return condition.conditions.some((c) => evaluatePreSalesVisibleIf(c, answers));
    case 'and':
      return condition.conditions.every((c) => evaluatePreSalesVisibleIf(c, answers));
    case 'not':
      return !evaluatePreSalesVisibleIf(condition.condition, answers);
    case 'includes': {
      const answer = getAnswer(answers, condition.questionKey);
      if (typeof answer === 'string') {
        return answer.includes(condition.value);
      }
      if (Array.isArray(answer)) {
        return answer.includes(condition.value);
      }
      return false;
    }
    case 'in': {
      const answer = getAnswer(answers, condition.questionKey);
      return condition.values.some((value) => arePreSalesValuesEqual(answer, value));
    }
    case 'arrayIncludes': {
      const answer = getAnswer(answers, condition.questionKey);
      return Array.isArray(answer) && answer.includes(condition.value);
    }
    case 'arrayIncludesAny': {
      const answer = getAnswer(answers, condition.questionKey);
      if (!Array.isArray(answer)) return false;
      return condition.values.some((v) => answer.includes(v));
    }
    case 'arrayNotIncludes': {
      const answer = getAnswer(answers, condition.questionKey);
      return !Array.isArray(answer) || !answer.includes(condition.value);
    }
    case 'eqExpression': {
      if (condition.expression === 'thresholdYear') {
        const thresholdYear = new Date().getFullYear() - 20;
        const answer = getAnswer(answers, condition.questionKey);
        const num = typeof answer === 'number' ? answer : typeof answer === 'string' ? Number(answer) : NaN;
        return !isNaN(num) && num === thresholdYear;
      }
      return false;
    }
    case 'defined': {
      const answer = getAnswer(answers, condition.questionKey);
      return answer !== undefined && answer !== null;
    }
    case 'prefixInRange': {
      const answer = getAnswer(answers, condition.questionKey);
      if (typeof answer !== 'string') return false;
      const len = condition.length ?? 2;
      const prefix = answer.slice(0, len);
      if (prefix.length < len || !/^\d+$/.test(prefix)) return false;
      const num = Number(prefix);
      return num >= condition.from && num <= condition.to;
    }
    case 'numberInRange': {
      const answer = getAnswer(answers, condition.questionKey);
      const num = typeof answer === 'number' ? answer : typeof answer === 'string' ? Number(answer) : NaN;
      if (isNaN(num)) return false;
      return num >= condition.from && num <= condition.to;
    }
    default:
      return false;
  }
};

export const evaluatePreSalesQuestionVisible = (question: PreSalesQuestion, answers: PreSalesAnswers): boolean => evaluatePreSalesVisibleIf(question.visibleIf, answers);

const evaluatePreSalesValidationRules = (rules: PreSalesValidationRule[] | undefined, value: unknown, answers: PreSalesAnswers): string | null => {
  if (!rules?.length) return null;

  for (const rule of rules) {
    switch (rule.type) {
      case 'required': {
        if (isPreSalesAnswerEmpty(value)) return rule.message;
        break;
      }
      case 'oneOf': {
        const set = rule.values;
        if (!set) break;
        if (value === undefined || value === null) return rule.message;
        const strVal = typeof value === 'string' ? value : String(value);
        if (!set.includes(strVal)) {
          return rule.message;
        }
        break;
      }
      case 'numberRange': {
        if (value === undefined || value === null) break;
        const num = typeof value === 'number' ? value : Number(value);
        if (isNaN(num)) {
          return rule.message ?? 'Bitte geben Sie eine gültige Zahl ein.';
        }
        if (num < rule.min || num > rule.max) {
          return rule.message ?? `Der Wert muss zwischen ${rule.min} und ${rule.max} liegen.`;
        }
        break;
      }
      case 'gtField': {
        const other = getAnswer(answers, rule.questionKey);
        if (value === undefined || value === null) break;
        if (other === undefined || other === null) break;
        const numVal = typeof value === 'number' ? value : Number(value);
        const numOther = typeof other === 'number' ? other : Number(other);
        if (isNaN(numVal) || isNaN(numOther)) break;
        if (numVal <= numOther) return rule.message;
        break;
      }
      case 'requiredIf': {
        const otherVal = getAnswer(answers, rule.questionKey);
        const match = Array.isArray(rule.value) ? rule.value.includes(otherVal as string) : otherVal === rule.value;
        if (!match) break;
        if (isPreSalesAnswerEmpty(value)) return rule.message;
        break;
      }
      case 'requiredIfArrayIncludes': {
        const arr = getAnswer(answers, rule.questionKey);
        if (!Array.isArray(arr) || !arr.includes(rule.value)) break;
        if (isPreSalesAnswerEmpty(value)) return rule.message;
        break;
      }
      case 'notAllowed': {
        if (!rule.value) break;
        const normalizedRuleValue = typeof rule.value === 'string' ? rule.value.toLowerCase() : rule.value;
        const normalizedValue = typeof value === 'string' ? value.toLowerCase() : value;
        if (normalizedRuleValue === normalizedValue) return rule.message;
        break;
      }
    }
  }

  return null;
};

export const evaluatePreSalesQuestionValidation = (question: PreSalesQuestion, value: unknown, answers: PreSalesAnswers, validateRequired: boolean): string | null => {
  if (validateRequired && isPreSalesAnswerEmpty(value)) {
    return 'Bitte beantworte diese Frage';
  }

  if (isPreSalesAnswerEmpty(value)) {
    return null;
  }

  return evaluatePreSalesValidationRules(question.validationRules, value, answers);
};

const evaluatePreSalesWarningChecks = (checks: PreSalesWarningCheck[] | undefined, value: unknown, _answers: PreSalesAnswers): string | null => {
  if (!checks?.length) return null;
  if (value === undefined || value === null) return null;

  const num = typeof value === 'number' ? value : Number(value);
  if (isNaN(num)) return null;

  for (const check of checks) {
    switch (check.type) {
      case 'numberBelow':
        if (check.warnBelow !== undefined && num < check.warnBelow) {
          return check.message;
        }
        break;
      case 'numberAbove':
        if (num > check.warnAbove) {
          return check.message;
        }
        break;
      case 'numberRange':
        if (check.warnBelow !== undefined && num < check.warnBelow) {
          return check.message;
        }
        if (check.warnAbove !== undefined && num > check.warnAbove) {
          return check.message;
        }
        break;
    }
  }

  return null;
};

export const evaluatePreSalesQuestionWarning = (question: PreSalesQuestion, value: unknown, answers: PreSalesAnswers): string | null =>
  evaluatePreSalesWarningChecks(question.warningChecks, value, answers);
